import React, {
	useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import styled from 'styled-components'
import {
	FaBars, FaChevronLeft, FaChevronRight, FaListUl, FaTrash, FaUserFriends, FaVoteYea,
} from 'react-icons/fa'

const STORAGE_KEY_PREFIX = 'respostasConsulta'
const ACTIONS = {
	manter: { label: 'Manter', needsText: false },
	revisar: { label: 'Revisar', needsText: true },
	remover: { label: 'Excluir', needsText: false },
	incluir: { label: 'Incluir', needsText: true },
}

const Root = styled.div`
	width: 100%;
`

const Header = styled.header`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 16px;
	margin-bottom: 24px;

	h2 {
		width: 100%;
		margin: 0;
		font-weight: 700;
	}

	@media (max-width: 768px) {
		flex-direction: column;
	}
`

const Counter = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	color: #546a7b;
	font-size: 0.78rem;
	font-weight: 700;
	letter-spacing: 0;
	text-transform: uppercase;
	white-space: nowrap;
`

const Layout = styled.div`
	display: grid;
	grid-template-columns: ${props => props.collapsed ? '44px minmax(0, 1fr)' : 'minmax(220px, 28%) minmax(0, 1fr)'};
	border: 1px solid #dde3ea;
	border-radius: 6px;
	background: #fff;

	@media (max-width: 768px) {
		grid-template-columns: 1fr;
	}
`

const ColumnHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex: none;
	height: 44px;
	padding: 10px 14px;
	background: #f3f6f8;
	border-bottom: 1px solid #dde3ea;
	color: #32485a;
	font-size: 0.82rem;
	font-weight: 700;
	text-transform: uppercase;
`

const ToggleButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 26px;
	height: 26px;
	border: 1px solid transparent;
	border-radius: 4px;
	background: transparent;
	color: inherit;
	cursor: pointer;

	:hover,
	:focus {
		border-color: #b8c5d0;
		background: #fff;
		outline: none;
	}
`

const Sidebar = styled.aside`
	border-right: 1px solid #dde3ea;
	background: #fbfcfd;

	@media (max-width: 768px) {
		border-right: 0;
		border-bottom: 1px solid #dde3ea;
	}
`

const IndexContainer = styled.nav`
	position: sticky;
	top: 0;
	max-height: 90vh;
	overflow: auto;
	padding: 8px 0 16px;

	ul {
		list-style: none;
		margin: 0;
		padding: 0;
	}
`

const IndexLink = styled.a`
	display: block;
	padding: 7px 12px;
	padding-left: ${props => 12 + props.depth * 14}px;
	color: ${props => props.active ? 'var(--primaryBg)' : '#275d86'};
	background: ${props => props.active ? 'rgba(var(--primaryBg-RGB), 0.09)' : 'transparent'};
	font-size: ${props => Math.max(0.78, 0.92 - props.depth * 0.035)}rem;
	font-weight: ${props => props.depth === 0 ? 700 : 500};
	line-height: 1.25;

	:hover {
		color: var(--primaryBg);
		text-decoration: underline;
	}
`

const Content = styled.main`
	min-width: 0;
	flex-direction: column;
`

const ContentBody = styled.div`
	padding: 22px;

	@media (max-width: 768px) {
		padding: 16px;
	}
`

const TrechoSection = styled.section`
	scroll-margin-top: 16px;
	margin-top: ${props => props.depth === 0 ? 0 : 18}px;
	padding-left: 18px;

	@media (max-width: 768px) {
		padding-left: 12px;
	}
`

const TrechoTitle = styled.h3`
	margin: 0 0 10px;
	color: #263846;
	font-size: ${props => Math.max(1, 1.35 - props.depth * 0.08)}rem;
	font-weight: 700 !important;
	line-height: 1.3;
`

const TrechoText = styled.p`
	margin: 0 0 14px;
	color: #2f3d48;
	line-height: 1.65;
	text-align: justify;
`

const ContributionCard = styled.div`
	margin: 12px 0 18px;
	padding: 14px;
	border: 1px solid #d8e0e7;
	border-radius: 6px;
	background: #f7f9fb;
	text-align: center;
`

const CardHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	margin-bottom: 12px;
	color: #334b5f;
	font-weight: 700;
`

const ButtonRow = styled.div`
	display: flex;
	justify-content: center;
	flex-wrap: wrap;
	gap: 8px;
`

const ActionButton = styled.button`
	border: 1px solid ${props => props.selected ? 'var(--primaryBg, var(--Branding-Color))' : '#aebdcc'};
	border-radius: 4px;
	background: ${props => props.selected ? 'var(--primaryBg, var(--Branding-Color))' : '#fff'};
	color: ${props => props.selected ? '#fff' : '#2d4d64'};
	padding: 8px 12px;
	font-weight: 700;
	cursor: pointer;

	:hover,
	:focus {
		border-color: var(--primaryBg, var(--Branding-Color));
		outline: none;
	}
`

const RemoveContributionButton = styled(ActionButton)`
	border-color: #c23030;
	color: #a82020;

	:hover,
	:focus {
		border-color: #a82020;
		background: #fff5f5;
	}
`

const TextAreaWrap = styled.div`
	margin-top: 12px;

	label {
		display: block;
		margin-bottom: 6px;
		color: #3f5363;
		font-weight: 700;
	}

	textarea {
		display: block;
		width: 100%;
		min-height: 110px;
		padding: 10px;
		border: 1px solid #b8c5d0;
		border-radius: 4px;
		resize: vertical;
	}
`

const ConfirmationText = styled.div`
	margin-top: 10px;
	padding: 10px 12px;
	border-left: 3px solid var(--primaryBg);
	background: #fff;
	white-space: pre-wrap;
	text-align: start;
`

const EmptyState = styled.p`
	margin: 0;
	color: #687985;
`

const RequiredProbe = styled.input`
	position: absolute;
	width: 1px;
	height: 1px;
	margin: 0;
	padding: 0;
	border: 0;
	opacity: 0;
	pointer-events: none;
	margin-left: 50%;
`

function storageKey(id) {
	return `${STORAGE_KEY_PREFIX}${id}`
}

function readStorage(id) {
	try {
		return JSON.parse(sessionStorage.getItem(storageKey(id)) || '{}')
	} catch {
		return {}
	}
}

function writeStorage(id, data) {
	sessionStorage.setItem(storageKey(id), JSON.stringify(data))
}

function hasActions(node) {
	const manifestar = node?.manifestar || {}
	return !!(manifestar.manterERevisar || manifestar.remover || manifestar.incluir)
}

function getAvailableActions(node) {
	const manifestar = node?.manifestar || {}
	const result = []
	if (manifestar.manterERevisar) result.push('manter', 'revisar')
	if (manifestar.remover) result.push('remover')
	if (manifestar.incluir) result.push('incluir')
	return result
}

function normalizeContribuicoes(value) {
	if (!value) return {}
	if (typeof value === 'string') {
		try {
			return JSON.parse(value) || {}
		} catch {
			return {}
		}
	}
	if (Array.isArray(value)) {
		return value.reduce((acc, item) => {
			const trechoId = item.trecho_id || item.trechoId || item.id_trecho || item.id
			if (trechoId) {
				acc[trechoId] = {
					acao: item.acao ?? item.tipo ?? item.manifestacao,
					texto: item.texto || item.resposta || item.conteudo || '',
				}
			}
			return {
				...acc,
				...normalizeContribuicoes(item.subtrechos),
			}
		}, {})
	}
	return value
}

function shouldUseAutoNumbering() {
	return String(process.env.REACT_APP_NORMA_NUMERACAO_AUTOMATICA).toLowerCase() === 'true'
}

function addOutlineNumbering(nodes, path = []) {
	return (nodes || []).map((node, index) => {
		const nextPath = [...path, index + 1]
		return {
			...node,
			titulo: `${nextPath.join('.')}. ${node.titulo}`,
			subtrechos: addOutlineNumbering(node.subtrechos, nextPath),
		}
	})
}

function flattenTrechos(nodes) {
	return (nodes || []).flatMap(node => [node, ...flattenTrechos(node.subtrechos)])
}

function nodeHasContribution(node, contribuicoes) {
	if (contribuicoes[node.id]) return true
	return (node.subtrechos || []).some(child => nodeHasContribution(child, contribuicoes))
}

export function getNormaTrechos(norma) {
	const trechos = norma?.trechos || []
	return shouldUseAutoNumbering() ? addOutlineNumbering(trechos) : trechos
}

export function salvarContribuicoesNormaLocalmente(id, contribuicoes) {
	writeStorage(id, {
		...readStorage(id),
		normaContribuicoes: contribuicoes,
		normaContribuicoesAlteradas: true,
	})
}

export function getNormaContribuicoesPayload(contribuicoes) {
	return Object.entries(contribuicoes || {}).map(([trechoId, contribuicao]) => ({
		id: Number(trechoId),
		tipo: contribuicao.acao,
		texto: contribuicao.texto || '',
	}))
}

export function hasContribuicaoNovaNorma(id) {
	const local = readStorage(id)
	const contribuicoes = normalizeContribuicoes(local.normaContribuicoes)
	return !!local.normaContribuicoesAlteradas &&
		Object.values(contribuicoes).some(contribuicao => contribuicao?.acao)
}

function NormaContributionCounter({ total }) {
	return <Counter>
		<FaUserFriends aria-hidden="true" />
		Recebemos {total || 0} contribuiç{total !== 1 ? 'ões' : 'ão'}
	</Counter>
}

function NormaTreeIndex({ nodes, activeId, depth = 0, onNavigate }) {
	return <ul>
		{nodes.map(node => <li key={node.id}>
			<IndexLink
				href={`#trecho-${node.id}`}
				depth={depth}
				active={activeId === node.id}
				onClick={onNavigate}
			>
				{node.titulo}
			</IndexLink>
			{!!node.subtrechos?.length && <NormaTreeIndex
				nodes={node.subtrechos}
				activeId={activeId}
				depth={depth + 1}
				onNavigate={onNavigate}
			/>}
		</li>)}
	</ul>
}

function NormaContributionButton({
	action, selected, disabled, onClick,
}) {
	return <ActionButton
		type="button"
		selected={selected}
		disabled={disabled}
		onClick={onClick}
	>
		{ACTIONS[action].label}
	</ActionButton>
}

function NormaContributionCard({
	node, value, disabled, onChange, minLength, maxLength, editOnly,
}) {
	const actions = getAvailableActions(node)
	if (!actions.length) return null

	const selectedAction = value?.acao
	const selectedConfig = selectedAction && ACTIONS[selectedAction]
	const showText = selectedConfig?.needsText

	if (disabled && !value) return null

	return <ContributionCard>
		<CardHeader>
			<FaVoteYea aria-hidden="true" />
			Contribuições
		</CardHeader>
		{disabled ? <>
			<strong>{selectedConfig?.label}</strong>
			{value?.texto && <ConfirmationText>{value.texto}</ConfirmationText>}
		</> : <>
			<ButtonRow>
				{actions.map(action => <NormaContributionButton
					key={action}
					action={action}
					selected={selectedAction === action}
					onClick={() => onChange(node.id, {
						acao: action,
						texto: value?.texto || '',
					})}
				/>)}
				{value && <RemoveContributionButton
					type="button"
					onClick={() => onChange(node.id, { acao: null, texto: '' })}
				>
					<FaTrash aria-hidden="true" /> Retirar contribuição
				</RemoveContributionButton>}
			</ButtonRow>
			{showText && <TextAreaWrap>
				<label htmlFor={`norma-trecho-${node.id}-texto`}>
					{selectedAction === 'incluir' ? 'Texto sugerido para inclusão' : 'Texto sugerido para revisão'}
				</label>
				<textarea
					id={`norma-trecho-${node.id}-texto`}
					name={`norma_trecho_${node.id}_texto`}
					value={value?.texto || ''}
					minLength={minLength}
					maxLength={maxLength}
					required
					onChange={event => onChange(node.id, {
						acao: selectedAction,
						texto: event.target.value,
					})}
				/>
			</TextAreaWrap>}
		</>}
	</ContributionCard>
}

function NormaTrechoNode({
	node, depth = 0, contribuicoes, disabled, onChange, minLength, maxLength, editOnly,
}) {
	const contributed = !!contribuicoes[node.id]
	if (disabled && !contributed && !nodeHasContribution(node, contribuicoes)) return null

	const Heading = `h${Math.min(depth + 2, 6)}`

	return <TrechoSection id={`trecho-${node.id}`} depth={depth}>
		<TrechoTitle as={Heading} depth={depth}>{node.titulo}</TrechoTitle>
		{node.texto && <TrechoText>{node.texto}</TrechoText>}
		{hasActions(node) && <NormaContributionCard
			node={node}
			value={contribuicoes[node.id]}
			disabled={disabled}
			onChange={onChange}
			minLength={minLength}
			maxLength={maxLength}
			editOnly={editOnly}
		/>}
		{!editOnly && (node.subtrechos || []).map(child => <NormaTrechoNode
			key={child.id}
			node={child}
			depth={depth + 1}
			contribuicoes={contribuicoes}
			disabled={disabled}
			onChange={onChange}
			minLength={minLength}
			maxLength={maxLength}
			editOnly={editOnly}
		/>)}
	</TrechoSection>
}

export default function FormContribuicaoNorma({
	id, nome, norma, quantidadeContribuicoes, disabled, formRef, submitRef, min_chars, max_chars, initialContribuicoes,
	remote, onSubmit, editOnly = false, submitImmediately = false,
}) {
	const nodes = useMemo(() => getNormaTrechos(norma), [norma])
	const allNodes = useMemo(() => flattenTrechos(nodes), [nodes])
	const [collapsed, setCollapsed] = useState(false)
	const [activeId, setActiveId] = useState(allNodes[0]?.id)
	const [contribuicoes, setContribuicoes] = useState(() => normalizeContribuicoes(initialContribuicoes))
	const hasUserChanged = useRef(false)
	const minContributionRef = useRef()
	const contributionCount = Object.keys(contribuicoes).filter(id => contribuicoes[id]?.acao).length
	const originalContributionIds = useMemo(
		() => Object.keys(normalizeContribuicoes(initialContribuicoes)),
		[initialContribuicoes]
	)
	console.log('FormContribuicaoNorma contribuicoes', contribuicoes, originalContributionIds, initialContribuicoes, allNodes)
	const editableNodes = useMemo(
		() => allNodes.filter(node => originalContributionIds.includes(String(node.id))),
		[allNodes, originalContributionIds]
	)
	const hasNewContribution = editOnly || (hasUserChanged.current
		? Object.keys(contribuicoes).length > 0
		: hasContribuicaoNovaNorma(id))

	useEffect(() => {
		setContribuicoes(normalizeContribuicoes(initialContribuicoes))
	}, [initialContribuicoes])

	useEffect(() => {
		if (editOnly) return undefined

		const observer = new IntersectionObserver(entries => {
			const visible = entries
				.filter(entry => entry.isIntersecting)
				.sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
			const id = visible?.target?.id?.replace('trecho-', '')
			if (id) setActiveId(Number(id))
		}, { rootMargin: '-20% 0px -70% 0px', threshold: [0, 0.1, 0.4] })

		allNodes.forEach(node => {
			const element = document.getElementById(`trecho-${node.id}`)
			if (element) observer.observe(element)
		})

		return () => observer.disconnect()
	}, [allNodes, editOnly])

	useEffect(() => {
		if (!disabled && hasUserChanged.current) salvarContribuicoesNormaLocalmente(id, contribuicoes)
	}, [id, contribuicoes, disabled])

	useEffect(() => {
		minContributionRef.current?.setCustomValidity(
			hasNewContribution ? '' : 'Selecione pelo menos uma nova contribuição em um trecho da norma.'
		)
	}, [hasNewContribution])

	const handleChange = useCallback((trechoId, nextValue) => {
		hasUserChanged.current = true
		setContribuicoes(current => {
			const next = { ...current }
			if (typeof nextValue.acao === 'undefined') delete next[trechoId]
			else next[trechoId] = nextValue
			return next
		})
	}, [])

	const handleSubmit = useCallback(event => {
		event.preventDefault()
		submitRef.current.target?.setCustomValidity('')

		if (!formRef.current?.reportValidity()) {
			submitRef.current.res?.(false)
			return
		}

		if (!disabled) {
			if (remote && !hasUserChanged.current) {
				submitRef.current.rej?.({
					icon: 'notify',
					title: 'Oops!',
					description: 'Você não realizou nenhuma alteração na sua contribuição.',
					action: 'close',
				})
				return
			}
			if (!submitImmediately) {
				submitRef.current.res?.()
				return
			}
		}

		onSubmit?.(getNormaContribuicoesPayload(contribuicoes))
	}, [disabled, submitImmediately, remote, formRef, submitRef, contribuicoes, onSubmit])

	const handleNavigate = useCallback(event => {
		event.preventDefault()
		const target = document.querySelector(event.currentTarget.getAttribute('href'))
		target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
	}, [])

	if (!nodes.length) return null

	return <Root>
		{!editOnly && <Header>
			<h2>{norma?.titulo || nome}</h2>
			<NormaContributionCounter total={quantidadeContribuicoes || 0} />
		</Header>}
		<form
			ref={formRef}
			id="formContrib"
			onSubmit={handleSubmit}
		>
			<RequiredProbe
				ref={minContributionRef}
				name="norma_contribuicao_minima"
				value={hasNewContribution ? 'ok' : ''}
				required
				onInvalid={evt => evt.target.setCustomValidity('Realize pelo menos uma contribuição.')}
				onChange={evt => evt.target.setCustomValidity('')}
			/>
			<input
				type="hidden"
				name="normaContribuicoes"
				value={JSON.stringify(contribuicoes)}
				readOnly
			/>
			{editOnly ? <ContentBody>
				{!editableNodes.length ? <EmptyState>Nenhum trecho contribuído.</EmptyState> : editableNodes.map(node => <NormaTrechoNode
					key={node.id}
					node={node}
					contribuicoes={contribuicoes}
					disabled={disabled}
					onChange={handleChange}
					minLength={min_chars}
					maxLength={max_chars}
					editOnly
				/>)}
			</ContentBody> : <Layout collapsed={collapsed}>
				<Sidebar>
					<ColumnHeader>
						<ToggleButton
							type="button"
							aria-label={collapsed ? 'Abrir índice' : 'Fechar índice'}
							onClick={() => setCollapsed(value => !value)}
						>
							{collapsed ? <FaChevronRight aria-hidden="true" /> : <FaChevronLeft aria-hidden="true" />}
						</ToggleButton>
						{!collapsed && <><FaBars aria-hidden="true" /> Índice</>}
					</ColumnHeader>
					{!collapsed && <IndexContainer aria-label="Índice da norma">
						<NormaTreeIndex nodes={nodes} activeId={activeId} onNavigate={handleNavigate} />
					</IndexContainer>}
				</Sidebar>
				<Content>
					<ColumnHeader><FaListUl aria-hidden="true" /> Texto</ColumnHeader>
					<ContentBody>
						{disabled && !contributionCount ? <EmptyState>Nenhum trecho contribuído.</EmptyState> : nodes.map(node => <NormaTrechoNode
							key={node.id}
							node={node}
							contribuicoes={contribuicoes}
							disabled={disabled}
							onChange={handleChange}
							minLength={min_chars}
							maxLength={max_chars}
						/>)}
					</ContentBody>
				</Content>
			</Layout>}
			<input
				type="submit"
				id="submitFormContribuicao"
				style={{ display: 'none' }}
			/>
		</form>
	</Root>
}
